import { useCallback, useEffect, useRef, useState } from 'react'
import {
  buildLabInsight,
  fetchBiomarkerHistory,
  fetchLabDrafts,
  fetchLabs,
  uploadLab,
  type InBodyRow,
  type LabDraft,
  type LabReport,
  type SavedLabValue,
} from '../lib/api'
import { Button, Group, Empty, ErrorBanner, LoadError, Spinner } from '../components/ui'
import { IconChevronLeft, IconCamera, IconExplain } from '../components/icons'
import { LabReview } from './LabReview'

interface HistoryPoint {
  taken_at: string
  value: number
  unit: string
  ref_low: number | null
  ref_high: number | null
}

export function LabsScreen({
  initialReportId,
  onBack,
  onReviewing,
}: {
  initialReportId?: string
  onBack: () => void
  /** Во время сверки прячем нижние вкладки: случайное касание стирало правки. */
  onReviewing?: (active: boolean) => void
}) {
  const [drafts, setDrafts] = useState<LabDraft[]>([])
  const [reports, setReports] = useState<LabReport[]>([])
  const [inbody, setInbody] = useState<InBodyRow[]>([])
  const [reviewing, setReviewing] = useState<LabDraft | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const reload = useCallback(async () => {
    try {
      const [draftsData, labsData] = await Promise.all([fetchLabDrafts(), fetchLabs()])
      setDrafts(draftsData.drafts)
      setReports(labsData.reports)
      setInbody(labsData.inbody)
      setError(null)

      // Пришли по кнопке из чата — сразу открываем нужный черновик.
      if (initialReportId) {
        const target = draftsData.drafts.find((draft) => draft.id === initialReportId)
        if (target) { setReviewing(target); onReviewing?.(true) }
      }
    } catch (cause) {
      setError((cause as Error).message)
    } finally {
      setLoading(false)
    }
  }, [initialReportId, onReviewing])

  useEffect(() => {
    void reload()
  }, [reload])

  const upload = async (file: File) => {
    setUploading(true)
    setError(null)
    try {
      const result = await uploadLab(file)
      const draft: LabDraft = {
        id: result.reportId,
        taken_at: result.parsed.taken_at ?? new Date().toISOString().slice(0, 10),
        lab_name: result.parsed.lab_name,
        kind: result.parsed.kind,
        raw_ai_response: result.parsed,
        source_file_path: result.fileStored ? 'saved' : null,
      }
      setReviewing(draft)
      onReviewing?.(true)
    } catch (cause) {
      setError((cause as Error).message)
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  if (reviewing) {
    return (
      <LabReview
        draft={reviewing}
        onDone={() => {
          setReviewing(null)
          onReviewing?.(false)
          setLoading(true)
          void reload()
        }}
      />
    )
  }

  if (error && loading) return <LoadError message={error} onRetry={() => void reload()} />
  if (loading) return <Spinner label="Загружаю анализы…" />

  return (
    <div className="flex flex-col gap-3 p-4 pb-nav">
      <button type="button" onClick={onBack} className="flex min-h-11 items-center gap-1 self-start text-sm text-ink-2">
        <IconChevronLeft size={16} />Здоровье
      </button>

      <input
        ref={fileInput}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) void upload(file)
        }}
      />

      <Button variant="filled" onClick={() => fileInput.current?.click()} disabled={uploading}>
        {uploading ? ('Распознаю бланк…') : (<><IconCamera size={18} />Загрузить анализ или InBody</>)}
      </Button>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {drafts.length > 0 && (
        <Group caption={`Ждут проверки · ${drafts.length}`}>
          {drafts.map((draft) => (
            <div
              key={draft.id}
              className="flex items-center gap-3 border-b border-ink-separator py-2.5 last:border-none"
            >
              <span className="min-w-0 flex-1 text-sm">
                {draft.kind === 'inbody' ? 'InBody' : (draft.lab_name ?? 'Анализ')}
                <span className="ml-2 text-ink-2">{draft.taken_at}</span>
              </span>
              <Button onClick={() => { setReviewing(draft); onReviewing?.(true) }}>Сверить</Button>
            </div>
          ))}
        </Group>
      )}

      {reports.map((report) => (
        <ReportCard key={report.id} report={report} />
      ))}

      {inbody.length > 0 && <InBodyCard rows={inbody} />}

      {reports.length === 0 && inbody.length === 0 && drafts.length === 0 && (
        <Group caption="Пока пусто">
          <Empty>
            Сфотографируй бланк анализов или лист InBody — распознаю показатели и буду показывать
            динамику. В чате с ботом так же: напиши «анализ», потом пришли фото.
          </Empty>
        </Group>
      )}
    </div>
  )
}

function ReportCard({ report }: { report: LabReport }) {
  const [openBiomarker, setOpenBiomarker] = useState<string | null>(null)
  const [insight, setInsight] = useState<string | null>(report.ai_interpretation)
  const [insightError, setInsightError] = useState<string | null>(null)
  const [thinking, setThinking] = useState(false)
  const outOfRange = report.lab_values.filter((value) => value.out_of_range).length

  const explain = async () => {
    setThinking(true)
    setInsightError(null)
    try {
      const result = await buildLabInsight(report.id)
      setInsight(result.text)
    } catch (cause) {
      setInsightError((cause as Error).message)
    } finally {
      setThinking(false)
    }
  }

  return (
    <Group caption={`${report.taken_at}${report.lab_name ? ` · ${report.lab_name}` : ''}`}>
      <p className="mb-2 text-xs text-ink-2">
        Показателей {report.lab_values.length}
        {outOfRange > 0 && <span className="text-sys-orange"> · вне нормы {outOfRange}</span>}
      </p>

      {groupByCategory(report.lab_values).map(([category, values]) => (
        <div key={category} className="mb-2">
          {category && (
            <div className="mb-1 mt-2 text-[11px] uppercase tracking-wide text-ink-2">
              {category}
            </div>
          )}
          {values.map((value) => (
            <div key={value.biomarker_id}>
              <button
                type="button"
                onClick={() =>
                  setOpenBiomarker((current) =>
                    current === value.biomarker_id ? null : value.biomarker_id,
                  )
                }
                className="w-full border-b border-ink-separator py-2.5 text-left text-sm last:border-none"
              >
                <div className="flex items-center gap-3">
                  <span className="min-w-0 flex-1">{value.biomarkers.canonical_name}</span>
                  <span
                    className={`numerals shrink-0 ${value.out_of_range ? 'text-sys-orange' : 'text-ink-2'}`}
                  >
                    {value.value} {value.unit}
                  </span>
                </div>
                <ReferenceBar value={value} />
              </button>
              {openBiomarker === value.biomarker_id && (
                <BiomarkerHistory biomarkerId={value.biomarker_id} current={value} />
              )}
            </div>
          ))}
        </div>
      ))}

      <div className="mt-3">
        {insight ? (
          <div className="rounded-xl bg-ink-surface2 p-3 text-sm leading-relaxed whitespace-pre-wrap">
            {insight}
          </div>
        ) : (
          <Button onClick={explain} disabled={thinking}>
            {thinking ? ('Читаю…') : (<><IconExplain size={16} />Объяснить простыми словами</>)}
          </Button>
        )}
        {insightError && <p className="mt-2 text-xs text-sys-red">{insightError}</p>}
      </div>
    </Group>
  )
}

/** Ключи — как в справочнике biomarkers.category, порядок — как читают бланк. */
const CATEGORY_LABELS: Record<string, string> = {
  обмен: 'Обмен веществ',
  липиды: 'Липиды',
  кровь: 'Кровь',
  железо: 'Железо',
  щитовидка: 'Щитовидная железа',
  гормоны: 'Гормоны',
  воспаление: 'Воспаление и сосуды',
  печень: 'Печень',
  почки: 'Почки',
  витамины: 'Витамины и минералы',
}
const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS)

/** Показатели без категории уезжают вниз: незнакомые заводятся без неё. */
function groupByCategory(values: SavedLabValue[]): [string, SavedLabValue[]][] {
  const groups = new Map<string, SavedLabValue[]>()
  for (const value of values) {
    const key = value.biomarkers.category ?? ''
    groups.set(key, [...(groups.get(key) ?? []), value])
  }
  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (!a) return 1
      if (!b) return -1
      const indexA = CATEGORY_ORDER.indexOf(a)
      const indexB = CATEGORY_ORDER.indexOf(b)
      return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB)
    })
    .map(([key, group]) => [key ? (CATEGORY_LABELS[key] ?? key) : '', group] as [string, SavedLabValue[]])
}

/**
 * Полоса нормы: где значение стоит внутри референса. При одном замере это
 * информативнее графика динамики, которого ещё нет.
 */
function ReferenceBar({ value }: { value: SavedLabValue }) {
  if (value.ref_low === null || value.ref_high === null || value.ref_high <= value.ref_low) {
    return null
  }

  const span = value.ref_high - value.ref_low
  // Поля по 25% с каждой стороны, чтобы выход за норму было видно, а не
  // упиралось в край шкалы.
  const position = ((value.value - value.ref_low) / span) * 50 + 25
  const clamped = Math.min(98, Math.max(2, position))

  return (
    <div className="relative mt-1.5 h-1 rounded-full bg-ink-surface2">
      <div className="absolute inset-y-0 left-1/4 right-1/4 rounded-full bg-sys-green/30" />
      <div
        className={`absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full ${
          value.out_of_range ? 'bg-sys-orange' : 'bg-sys-green'
        }`}
        style={{ left: `${clamped}%` }}
      />
    </div>
  )
}

function BiomarkerHistory({ biomarkerId, current }: { biomarkerId: string; current: SavedLabValue }) {
  const [points, setPoints] = useState<HistoryPoint[] | null>(null)

  useEffect(() => {
    void fetchBiomarkerHistory(biomarkerId)
      .then((data) => setPoints(data.points))
      .catch(() => setPoints([]))
  }, [biomarkerId])

  const reference =
    current.ref_low !== null || current.ref_high !== null
      ? `Норма по бланку: ${current.ref_low ?? '—'}–${current.ref_high ?? '—'} ${current.unit}`
      : 'Референс на бланке не указан'

  return (
    <div className="mb-2 rounded-xl bg-ink-surface2 p-3 text-xs">
      <div className="mb-2 text-ink-2">{reference}</div>
      {points === null && <div className="text-ink-2">Загружаю…</div>}
      {points?.length === 1 && (
        <div className="text-ink-2">
          Одно измерение — динамику будет видно со второго анализа.
        </div>
      )}
      {points && points.length > 1 && mixedUnits(points) && (
        <div className="mb-2 text-sys-orange">
          Единицы в бланках разные — сравнивать эти значения между собой нельзя.
        </div>
      )}
      {points && points.length > 1 && (
        <div className="flex flex-col gap-1">
          {points.map((point) => {
            const out =
              (point.ref_low !== null && point.value < point.ref_low) ||
              (point.ref_high !== null && point.value > point.ref_high)
            return (
              <div key={point.taken_at} className="flex justify-between">
                <span className="text-ink-2">{point.taken_at}</span>
                <span className={`numerals ${out ? 'text-sys-orange' : ''}`}>
                  {point.value} {point.unit}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Автоматически конвертировать единицы нельзя: коэффициент свой у каждого
 * вещества (глюкоза ×18, холестерин ×38.67), и ошибка здесь незаметна.
 * Честнее предупредить, что точки несравнимы.
 */
function mixedUnits(points: HistoryPoint[]): boolean {
  return new Set(points.map((point) => point.unit.toLowerCase().trim())).size > 1
}

function InBodyCard({ rows }: { rows: InBodyRow[] }) {
  const latest = rows[0]
  if (!latest) return null
  const previous = rows[1]

  const delta = (key: 'weight_kg' | 'skeletal_muscle_kg' | 'body_fat_kg' | 'body_fat_pct') => {
    const now = latest[key]
    const before = previous?.[key]
    if (now === null || before === null || before === undefined) return null
    const diff = Math.round((now - before) * 10) / 10
    if (diff === 0) return 'без изменений'
    return `${diff > 0 ? '+' : ''}${diff}`
  }

  const lines: { label: string; value: number | null; unit: string; change: string | null }[] = [
    { label: 'Вес', value: latest.weight_kg, unit: 'кг', change: delta('weight_kg') },
    {
      label: 'Скелетные мышцы',
      value: latest.skeletal_muscle_kg,
      unit: 'кг',
      change: delta('skeletal_muscle_kg'),
    },
    { label: 'Жировая масса', value: latest.body_fat_kg, unit: 'кг', change: delta('body_fat_kg') },
    { label: 'Процент жира', value: latest.body_fat_pct, unit: '%', change: delta('body_fat_pct') },
    { label: 'Висцеральный жир', value: latest.visceral_fat_level, unit: 'ур.', change: null },
    { label: 'Основной обмен', value: latest.bmr_kcal, unit: 'ккал', change: null },
  ]

  return (
    <Group caption={`InBody · ${latest.taken_at}`}>
      {lines
        .filter((line) => line.value !== null)
        .map((line) => (
          <div
            key={line.label}
            className="flex items-center gap-3 border-b border-ink-separator py-2.5 text-sm last:border-none"
          >
            <span className="min-w-0 flex-1">{line.label}</span>
            <span className="numerals shrink-0">
              {line.value} {line.unit}
              {line.change && <span className="ml-2 text-ink-2">{line.change}</span>}
            </span>
          </div>
        ))}
      {rows.length === 1 && (
        <p className="mt-2 text-[11px] text-ink-2">
          Первый замер. Сравнение появится после второго — интервал имеет смысл делать не чаще
          раза в месяц.
        </p>
      )}
    </Group>
  )
}
